import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  InputAdornment,
  Autocomplete,
  CircularProgress,
  Avatar,
} from "@mui/material";
import { Search, RemoveCircleOutline } from "@mui/icons-material";
import { styled } from "@mui/system";
import { useNavigate } from "react-router-dom";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useAuth } from "../../../../../context";
import { useTranslation } from "react-i18next";
import { getLocalDateString } from "../../../../../utils";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

// Styled Components
const FullWidthPaper = styled(Paper)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#ffffff",
  boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.1)",
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.5rem",
  fontWeight: 700,
  color: "#3a3a3a",
  textAlign: "left",
  marginBottom: theme.spacing(4),
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: "#fff",
  fontWeight: 700,
  fontSize: "1rem",
  padding: "12px 16px",
  borderRadius: theme.shape.borderRadius,
  boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const LaboratoryReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const today = getLocalDateString();
  const [ticketSearch, setTicketSearch] = useState("");
  const selectedTicketSnapshot = useRef(null);

  // Doctor selection state
  const [doctorPage, setDoctorPage] = useState(1);
  const [doctorPageSize] = useState(100);
  const [doctorSearchTerm, setDoctorSearchTerm] = useState("");
  const [allDoctors, setAllDoctors] = useState([]);
  const [hasMoreDoctors, setHasMoreDoctors] = useState(true);
  const [doctorInputValue, setDoctorInputValue] = useState("");

  const { data: laboratoryTestsData } = useFetchObjects(
    ["laboratory-tests"],
    "laboratory-tests/",
    token
  );

  const effectiveTicketSearch = selectedTicketSnapshot.current ? "" : ticketSearch.trim();
  const shouldSearchTickets = effectiveTicketSearch.length >= 3;
  const { data: ticketsData, isLoading: isLoadingTickets } = useFetchObjects(
    ["tickets-referral-reception", effectiveTicketSearch],
    `tickets/?search=${encodeURIComponent(effectiveTicketSearch)}&page_size=200`,
    token,
    { enabled: !!token && shouldSearchTickets }
  );
  const tickets = shouldSearchTickets ? (ticketsData?.results ?? (Array.isArray(ticketsData) ? ticketsData : [])) : [];

  const {
    data: doctorsData,
    isLoading: isLoadingDoctors,
    refetch: refetchDoctors,
  } = useFetchObjects(
    ["users", doctorPage, doctorPageSize, doctorSearchTerm],
    `users/?page=${doctorPage}&page_size=${doctorPageSize}&search=${doctorSearchTerm}`,
    token
  );

  const { handleAdd, resetForm, loading } = useAdd(
    "laboratory-test-orders",
    token
  );

  const laboratoryTests = laboratoryTestsData || [];

  const [searchText, setSearchText] = useState("");
  const [filteredTests, setFilteredTests] = useState([]);
  const [selectedTests, setSelectedTests] = useState([]);
  const [labDetails, setLabDetails] = useState({
    ticket: null,
    doctor: null,
    urgency: false,
    discount: 0,
  });

  const [errors, setErrors] = useState({});

  // Handle doctors data
  useEffect(() => {
    // Check if data exists and is paginated
    if (doctorsData?.results && Array.isArray(doctorsData.results)) {
      // If we're on page 1, replace the list
      // Otherwise, append for pagination
      if (doctorPage === 1) {
        setAllDoctors(doctorsData.results);
      } else {
        setAllDoctors((prevDoctors) => {
          const newDoctors = [...prevDoctors, ...doctorsData.results];
          const uniqueDoctors = newDoctors.reduce((acc, current) => {
            if (!acc.find((item) => item.id === current.id)) {
              acc.push(current);
            }
            return acc;
          }, []);
          return uniqueDoctors;
        });
      }
      setHasMoreDoctors(doctorsData.next !== null);
    } else if (doctorsData && Array.isArray(doctorsData)) {
      // Handle case where API returns array directly (no pagination)
      if (doctorPage === 1) {
        setAllDoctors(doctorsData);
      } else {
        setAllDoctors((prevDoctors) => {
          const newDoctors = [...prevDoctors, ...doctorsData];
          const uniqueDoctors = newDoctors.reduce((acc, current) => {
            if (!acc.find((item) => item.id === current.id)) {
              acc.push(current);
            }
            return acc;
          }, []);
          return uniqueDoctors;
        });
      }
      setHasMoreDoctors(false);
    } else if (!doctorsData) {
      // No data yet, keep current state
      return;
    } else {
      setHasMoreDoctors(false);
    }
  }, [doctorsData, doctorPage]);

  // Handle search term changes for doctors - reset list and page
  useEffect(() => {
    setAllDoctors([]);
    setDoctorPage(1);
    setHasMoreDoctors(true);
    // React Query will automatically refetch when doctorSearchTerm changes
    // because it's in the queryKey array
  }, [doctorSearchTerm]);

  useEffect(() => {
    if (laboratoryTests && searchText) {
      const filtered = laboratoryTests.filter(
        (test) =>
          test.name.toLowerCase().includes(searchText.toLowerCase()) &&
          (test.is_parent_test === true || test.is_parent_test === undefined)
      );
      setFilteredTests(filtered);
    } else {
      setFilteredTests([]);
    }
  }, [searchText, laboratoryTests]);

  useEffect(() => {
    if (resetForm) {
      setLabDetails({
        ticket: null,
        doctor: null,
        urgency: false,
        discount: 0,
      });
      setSelectedTests([]);
      setSearchText("");
      setErrors({});
      selectedTicketSnapshot.current = null;
    }
  }, [resetForm]);

  const debouncedSetDoctorSearchTerm = useCallback(
    debounce((value) => {
      setDoctorSearchTerm(value);
    }, 300),
    []
  );

  // Doctor handlers
  const handleDoctorInputChange = (event, newInputValue) => {
    setDoctorInputValue(newInputValue);
    debouncedSetDoctorSearchTerm(newInputValue);
  };

  const handleDoctorChange = (event, newValue) => {
    setLabDetails((prev) => ({ ...prev, doctor: newValue }));
    setErrors((prev) => ({ ...prev, doctor: "" }));
  };

  const handleDoctorScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMoreDoctors &&
      !isLoadingDoctors
    ) {
      setDoctorPage((prevPage) => prevPage + 1);
    }
  };

  const handleAddNewTest = (test) => {
    if (test) {
      if (!selectedTests.find((t) => t.id === test.id)) {
        setSelectedTests((prevTests) => [...prevTests, test]);
      }
      setSearchText("");
    }
  };

  const handleRemoveTest = (index) => {
    const updatedTests = [...selectedTests];
    updatedTests.splice(index, 1);
    setSelectedTests(updatedTests);
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setLabDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const calculateTotal = () => {
    let totalCost = 0;
    selectedTests.forEach((test) => {
      if (test.cost) {
        totalCost += parseFloat(test.cost);
      }
    });
    const discount = parseFloat(labDetails.discount) || 0;
    return Math.max(0, totalCost - discount).toFixed(2);
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!labDetails.ticket) {
      tempErrors.ticket = t("doctor.selectTicketRequired");
    }
    if (!labDetails.doctor) {
      tempErrors.doctor =
        t("doctor.doctorRequired") || "Doctor selection is required.";
    }
    if (selectedTests.length === 0) {
      tempErrors.tests =
        t("laboratory.atLeastOneTestRequired") ||
        "At least one test is required.";
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const patientId = typeof labDetails.ticket.patient === "object" ? labDetails.ticket.patient?.id : labDetails.ticket.patient;
      const payload = {
        patient: patientId,
        ticket: labDetails.ticket.id,
        doctor: labDetails.doctor.id,
        tests: selectedTests.map((test) => ({ test_type: test.id })),
        urgency: labDetails.urgency,
        discount: parseFloat(labDetails.discount) || 0,
      };
      handleAdd(payload);
    }
  };

  const getDoctorInitials = (doctor) => {
    if (!doctor) return "?";
    const firstInitial = doctor.first_name ? doctor.first_name.charAt(0) : "";
    const lastInitial = doctor.last_name ? doctor.last_name.charAt(0) : "";
    return (firstInitial + lastInitial).toUpperCase();
  };

  return (
    <FullWidthPaper>
      <SectionTitle>
        {t("doctor.labReferralForm") || "Laboratory Test Referral Form"}
      </SectionTitle>

      <Grid container spacing={3}>
        {/* Ticket selection – patient comes from ticket */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={tickets}
            noOptionsText={shouldSearchTickets ? "No options" : "Type at least 3 characters to search"}
            getOptionLabel={(option) =>
              option ? `${option.ticket_number || option.id}${option.patient_name_display ? ` - ${option.patient_name_display}` : ""}` : ""
            }
            value={labDetails.ticket}
            inputValue={ticketSearch}
            onInputChange={(e, value) => {
              setTicketSearch(value);
              if (!value) selectedTicketSnapshot.current = null;
            }}
            onChange={(event, newValue) => {
              selectedTicketSnapshot.current = newValue ? { id: newValue.id } : null;
              setLabDetails((prev) => ({ ...prev, ticket: newValue }));
              setErrors((prev) => ({ ...prev, ticket: "" }));
            }}
            loading={isLoadingTickets}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("print.ticketNumber")}
                placeholder={t("doctor.selectTicketPlaceholder")}
                variant="outlined"
                fullWidth
                error={!!errors.ticket}
                helperText={errors.ticket}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingTickets ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
        </Grid>
        {labDetails.ticket && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("laboratory.patientName")}
              value={labDetails.ticket.patient_name_display ?? ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        {/* Doctor Autocomplete */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={allDoctors || []}
            getOptionLabel={(option) =>
              option
                ? `${option.first_name || ""} ${option.last_name || ""}`.trim()
                : ""
            }
            inputValue={doctorInputValue}
            onInputChange={handleDoctorInputChange}
            onChange={handleDoctorChange}
            value={labDetails.doctor}
            ListboxProps={{
              onScroll: handleDoctorScroll,
            }}
            loading={isLoadingDoctors}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  padding: 1.5,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Avatar
                  sx={{ bgcolor: "secondary.main", width: 40, height: 40 }}
                >
                  {getDoctorInitials(option)}
                </Avatar>
                <Typography variant="body1" fontWeight={600}>
                  {`${option.first_name || ""} ${
                    option.last_name || ""
                  }`.trim()}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("doctor.selectDoctor") || "Select Doctor"}
                variant="outlined"
                fullWidth
                error={!!errors.doctor}
                helperText={errors.doctor}
                placeholder={t(
                  "reception.searchDoctorByName",
                  "Search by Doctor Name"
                )}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingDoctors ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Grid>

        {/* Urgency Switch */}
        <Grid item xs={12} sm={6}>
          <FormControlLabel
            control={
              <Switch
                checked={labDetails.urgency}
                onChange={handleChange}
                name="urgency"
              />
            }
            label={t("doctor.urgent")}
          />
        </Grid>

        {/* Test Selection */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            {t("laboratory.addTest") || "Select Tests"}
          </Typography>
          <Autocomplete
            value={null}
            inputValue={searchText}
            onInputChange={(event, newInputValue) =>
              setSearchText(newInputValue)
            }
            onChange={(event, value) => handleAddNewTest(value)}
            options={filteredTests}
            getOptionLabel={(option) =>
              `${option.name}${
                option.category_display
                  ? ` (${option.category_display.name})`
                  : ""
              }`
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("laboratory.searchTest") || "Search Test"}
                placeholder={
                  t("laboratory.searchTestPlaceholder") ||
                  "Search for laboratory tests"
                }
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
          {errors.tests && (
            <Typography
              color="error"
              variant="caption"
              sx={{ mt: 1, display: "block" }}
            >
              {errors.tests}
            </Typography>
          )}
        </Grid>

        {/* Selected Tests Table */}
        {selectedTests.length > 0 && (
          <Grid item xs={12}>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead sx={{ backgroundColor: "#eef2f5" }}>
                  <TableRow>
                    <TableCell>{t("laboratory.testName")}</TableCell>
                    <TableCell align="center">
                      {t("laboratory.testCost")}
                    </TableCell>
                    <TableCell align="center">
                      {t("laboratory.action")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "#ccfcfc" }}>
                  {selectedTests.map((test, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{test.name}</TableCell>
                      <TableCell align="center">
                        {test.cost || "N/A"}؋
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={t("laboratory.removeTest")} arrow>
                          <IconButton
                            color="error"
                            onClick={() => handleRemoveTest(index)}
                          >
                            <RemoveCircleOutline />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        )}

        {/* Discount and Total */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="discount"
            label={t("laboratory.discount")}
            type="number"
            fullWidth
            value={labDetails.discount}
            onChange={handleChange}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              justifyContent: "flex-end",
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              {t("laboratory.totalAmount")}: {calculateTotal()}؋
            </Typography>
          </Box>
        </Grid>

        {/* Submit Button */}
        <Grid item xs={12}>
          <StyledButton fullWidth onClick={handleSubmit} disabled={loading}>
            {loading ? t("laboratory.saving") : t("doctor.submit")}
          </StyledButton>
        </Grid>
      </Grid>
    </FullWidthPaper>
  );
};

export default LaboratoryReferral;




